import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, LogOut, AlertCircle, Send, Plus } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";

export default function BackofficeEscalationsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState(null); // {case_id, idx, question}
  const [answer, setAnswer] = useState("");
  const [addToKb, setAddToKb] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setRows(await api.escalationsList(!showAll)); } catch { setRows([]); }
  }
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line

  async function submit() {
    if (!answer.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.answerEscalation(active.case_id, active.idx, answer.trim(), addToKb);
      toast.success(
        r.draft_kb_entry
          ? "Answer sent to founder + DRAFT KB entry created"
          : "Answer sent to founder"
      );
      setActive(null);
      setAnswer("");
      setAddToKb(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send answer");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-slate-900">
            <Building2 size={22} className="text-[#C5283F]" />
            <span className="font-display text-xl font-semibold">Infin · Back office</span>
          </Link>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/admin/kb" className="text-slate-600 hover:text-slate-900">KB</Link>
            <Link to="/admin" className="text-slate-600 hover:text-slate-900">Cases</Link>
            <span className="text-slate-500">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }}
                    className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Escalations</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Questions Infi has flagged for our Company Secretary.
              Stale items (&gt;24h) are highlighted in red.
            </p>
          </div>
          <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showAll}
                   onChange={(e) => setShowAll(e.target.checked)}
                   data-testid="esc-show-all" />
            Show closed too
          </label>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {rows === null && <div className="p-8 text-sm text-slate-500">Loading…</div>}
          {rows && rows.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm">
              {showAll ? "No escalations yet." : "No open escalations — nice."}
            </div>
          )}
          {(rows || []).map((r) => (
            <div key={`${r.case_id}-${r.escalation_index}`}
                 data-testid={`esc-row-${r.case_id}-${r.escalation_index}`}
                 className={[
                   "px-6 py-4 border-b border-slate-100 last:border-0",
                   r.stale ? "bg-red-50/40" : "",
                 ].join(" ")}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {r.stale && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            data-testid={`esc-stale-${r.case_id}-${r.escalation_index}`}>
                        <AlertCircle size={10} /> Stale · {r.age_hours}h
                      </span>
                    )}
                    {!r.stale && !r.answered && (
                      <span className="text-[10px] uppercase tracking-wider font-medium text-slate-500">
                        {r.age_hours}h open
                      </span>
                    )}
                    {r.answered && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                        Answered
                      </span>
                    )}
                    <Link to={`/admin/cases/${r.case_id}`}
                          className="text-[11px] font-mono text-slate-500 hover:text-slate-900 underline">
                      {r.case_id}
                    </Link>
                    {r.company_name && (
                      <span className="text-[11px] text-slate-500">· {r.company_name}</span>
                    )}
                    {r.applicant_email && (
                      <span className="text-[11px] text-slate-400">· {r.applicant_email}</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-900 font-medium">{r.question}</div>
                  {r.answered && r.answer && (
                    <div className="mt-2 text-sm text-slate-700 bg-emerald-50 border-l-2 border-emerald-300 px-3 py-2 rounded">
                      <span className="font-semibold text-emerald-800">CS answer: </span>{r.answer}
                    </div>
                  )}
                </div>
                {!r.answered && (
                  <button onClick={() => { setActive(r); setAnswer(""); setAddToKb(false); }}
                          data-testid={`esc-answer-btn-${r.case_id}-${r.escalation_index}`}
                          className="bg-[#C5283F] text-white px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#A82035] shrink-0">
                    <Send size={11} /> Answer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl"
               onClick={(e) => e.stopPropagation()}
               data-testid="esc-answer-modal">
            <div className="font-display text-lg font-semibold mb-1">Answer this escalation</div>
            <div className="text-xs text-slate-500 mb-4 font-mono">{active.case_id}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Founder's question</div>
            <div className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              {active.question}
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Your answer (CS-verified)</label>
            <textarea value={answer} rows={6} autoFocus
                      onChange={(e) => setAnswer(e.target.value)}
                      data-testid="esc-answer-text"
                      placeholder="Write a clear, plain-language answer the founder can act on…"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3" />
            <label className="flex items-center gap-2 cursor-pointer mb-4">
              <input type="checkbox" checked={addToKb}
                     onChange={(e) => setAddToKb(e.target.checked)}
                     data-testid="esc-add-to-kb"
                     className="h-4 w-4 accent-[#C5283F]" />
              <span className="text-sm text-slate-700 inline-flex items-center gap-1">
                <Plus size={11} /> Also create a DRAFT KB entry from this Q&A
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setActive(null)}
                      className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm">Cancel</button>
              <button onClick={submit}
                      disabled={!answer.trim() || busy}
                      data-testid="esc-submit"
                      className="bg-[#C5283F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#A82035] disabled:opacity-50">
                {busy ? "Sending…" : "Send to founder"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
